import IndexService from "../../Services/http/IndexService";
import AccountService from "../../Services/http/AccountService";
import { TInactiveAccountResponse } from "../../types/Account.type";

const application_url = "https://sellercentral.amazon.com/apps/authorize/consent";
const application_id = 'amzn1.sp.solution.ede838cd-808b-4295-9026-c7400a165ed1';
export const createAwsStateAndNavigateToAws = async (account_id: number, isPartnerLoggedIn: boolean) => {
    try {
        const res = await IndexService.createAwsState(account_id);
        if (res && res["message"] === "success") {
            const searchParams = new URLSearchParams();
            searchParams.append('application_id', application_id);
            for (const key in res['params']) {
                searchParams.append(key, res['params'][key]);
            }
            const queryString = searchParams.toString();
            const url = `${application_url}?${queryString}`;
            if (isPartnerLoggedIn) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                window.location.replace(url);
            }
        } else {
            const errorMessage = res?.message;
            if (errorMessage === "account not found") {
                alert("account not found");
            } else if (errorMessage === "account invalid") {
                alert("account invalid");
            } else {
                alert("An error occurred during the AWS login");
            }
        }
    } catch (err) {
        console.error(err);
        alert("An error occurred during the AWS login");
    }
};

/**
 * Verify AWS authorization completion by polling checkIsInactiveAccount.
 *
 * Logic:
 * - Authorized == is_inactive_account === false
 * - Poll with exponential-ish backoff (gentle)
 * - Stop after maxAttempts OR maxTotalMs (whichever comes first)
 * - Optionally fail fast on "permanent" errors if backend provides them.
 */
export const verifyAwsAuthorizationComplete = async (
    account_id: number,
    {
        maxAttempts = 12,
        baseIntervalMs = 2000,
        backoffFactor = 1.4,   // gentle backoff
        maxIntervalMs = 8000,  // cap the backoff
        maxTotalMs = 60_000,   // hard stop after 60s
        failFastErrors = ["account not found", "account invalid"] as string[],
    } = {}
): Promise<boolean> => {
    const start = Date.now();

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        // Hard time stop (prevents “forever” even if someone changes maxAttempts)
        const elapsed = Date.now() - start;
        if (elapsed >= maxTotalMs) {
            throw new Error("Authorization verification timeout");
        }
        let res: TInactiveAccountResponse | null = null;
        try {
            res = (await AccountService.checkIsInactiveAccount(account_id));
        } catch {
            res = null;
        }

        // If backend tells us a permanent error, fail immediately
        const backendMsg = res?.error || res?.message;
        if (backendMsg && failFastErrors.includes(backendMsg)) {
            throw new Error(backendMsg);
        }

        // Success condition:
        // is_inactive_account === false  => refresh_token exists => authorized
        if (res && res.is_inactive_account === false) {
            console.log("AWS authorization verified complete", res);
            return true;
        }

        // If this was the last attempt, stop
        if (attempt === maxAttempts) {
            throw new Error("Authorization verification timeout");
        }

        // Compute backoff wait
        const rawWait = baseIntervalMs * Math.pow(backoffFactor, attempt - 1);
        const waitMs = Math.min(rawWait, maxIntervalMs);

        // Sleep before next attempt
        await new Promise((r) => setTimeout(r, waitMs));
    }

    // Should never reach here, but TS wants a return/throw
    throw new Error("Authorization verification timeout");
};
